import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Loader } from '../loader';
import { ValidationError } from '../validationError';
import { queries } from 'src/config/queries';
import { UploadFileHelper } from 'src/shared/helpers/uploadFile';
import { useUploadFile } from 'src/shared/hooks/useUploadFile';
import { Dropzone } from './dropzone';
import { FileDetails } from './fileDetails';
import { FilePreview } from './filePreview';
import { Instructions } from './instructions';
import { Pending } from './pending';
export function FileUpload({ uploadAttemptKey, onSelectFile, onStart, onEnd, details, entityType, fileName }) {
    const { t } = useTranslation();
    const { uploadFile } = useUploadFile();
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const uploadQuery = useQuery(queries.upload.config(entityType));
    const uploadAttemptKeyRef = useRef(uploadAttemptKey);
    useEffect(function handleUploadAttemptKeyChange() {
        uploadAttemptKeyRef.current = uploadAttemptKey;
        setIsLoading(false);
    }, [uploadAttemptKey]);
    async function upload(file) {
        if (!file || !uploadQuery.data)
            return;
        if (!UploadFileHelper.isValidFileExtension(file?.type, uploadQuery.data?.supportedFiles)) {
            setError(t('upload_file.validations.file_format'));
            return;
        }
        if (!UploadFileHelper.isValidFileSize(file.size, uploadQuery.data?.byteSizeLimit)) {
            setError(t('upload_file.validations.file_size'));
            return;
        }
        onStart?.();
        setIsLoading(true);
        const thisUploadAttemptKey = uploadAttemptKeyRef.current;
        const { fileId, filePreviewUrl } = await uploadFile(file, entityType);
        const isSameUploadAttempt = thisUploadAttemptKey === uploadAttemptKeyRef.current;
        if (isSameUploadAttempt) {
            onSelectFile({ id: fileId, name: file.name, url: filePreviewUrl || '' });
            onEnd?.(fileId);
        }
        setIsLoading(false);
    }
    function remove(e) {
        e.stopPropagation();
        setError('');
        onSelectFile(null);
    }
    if (uploadQuery.status === 'pending') {
        return _jsx(Loader, { full: "screen", "data-testid": "upload-loader", className: "my-12" });
    }
    return (_jsxs("div", { children: [details && (_jsx(FileDetails, { ...details, size: UploadFileHelper.convertByteToMegabyte(uploadQuery.data?.byteSizeLimit ?? 0) })), isLoading && _jsx(Pending, {}), fileName && _jsx(FilePreview, { onRemove: remove, fileName: fileName }), !isLoading && !fileName && (_jsxs("div", { children: [_jsx(Dropzone, { isDragging: isDragging, setIsDragging: setIsDragging, onSetFile: upload, children: _jsx(Instructions, { onSetFile: upload, isDragging: isDragging }) }), _jsx(ValidationError, { message: error })] }))] }));
}
